/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';

/**
 * A hook used to store the previous value of a state from a
 * previous render cycle. Useful for comparing this cycle's
 * state value and the previous ones.
 *
 * @param value The value to track.
 * @param transform An optional function to apply before assigning
 * the new value when it changes.
 * @param initialValue An optional initial value to add to the ref.
 * @returns A reference to the value in the previous render cycle.
 */
export const usePrevious = <T>(value: T, transform?: (val: T) => T, initialValue?: T) => {
    const ref = React.useRef<T>(initialValue);

    React.useEffect(() => {
        if (transform) {
            ref.current = transform(value);
        } else {
            ref.current = value;
        }
    }, [value]);

    return ref.current;
};
